import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import srcSpinner from "../../assets/images/blue-spinner.gif";
import { Artifact, Place } from "../../models";
import dateFormat from "../../utils/date";
import LutronDesignerClientProxy from "../../backend/clients/LutronDesignerClientProxy";
import { useServiceWorkerContext } from "../ServiceWorkerContext/ServiceWorkerContext";
import Modal from "../../ui/Modal/Modal";
import { TableMoreIconContainer } from "../ArtifactKebabMenu/ArtifactKebabMenu";

import DesignTable from "./DesignTable";
import OpenArtifactCell from "./OpenArtifactCell";
import ProductTypeBadges from "./ProductTypeBadges";
import { ActionButton, TableCell, TableRow } from "./TableStyle";

const Loader = styled.img`
  width: 100px;
  height: 100px;
`;

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Body = styled.tbody<{ isActive?: boolean }>`
  & > tr {
    padding: 10px 0px;
  }

  & > tr:first-of-type:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  & > tr:first-of-type {
    background: ${({ isActive }) =>
      isActive ? "rgba(0, 0, 0, 0.04)" : "transparent"};
  }

  ${ActionButton}, ${TableMoreIconContainer} {
    visibility: hidden;
  }

  & > tr:first-of-type ${ActionButton} {
    visibility: ${({ isActive }) => (isActive ? "visible" : "hidden")};
  }

  & > tr:first-of-type:hover ${ActionButton}, & > tr:hover ${TableMoreIconContainer}, ${TableMoreIconContainer}[data-open="true"] {
    visibility: visible;
  }
`;

const PlaceNameTableCell = styled(TableCell)`
  font-weight: 500;
`;

interface Props {
  place: Place;
  artifacts: Artifact[];
}

const PlaceRow = ({ place, artifacts }: Props): JSX.Element => {
  const sw = useServiceWorkerContext();
  const { t } = useTranslation();
  const [openBtnLoader, setOpenBtnLoader] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string>("");

  const handleArtifactClick = (artifact: Artifact) => {
    setOpenBtnLoader(true);
    const openArtifact = {
      artifactID: artifact.id,
      etag: "",
      guiGuid: "",
      guiMD5: "",
      lastModified: artifact.modifiedDate,
      artifactHref: artifact.href,
      orchestratorPayload: "",
    };
    LutronDesignerClientProxy.openArtifactRequest(
      sw.waitForInstalled,
      openArtifact,
    );
  };

  return (
    <Body
      isActive={expandedRowId === place.id}
      data-testid={`place-row[${place.id}]`}
    >
      {openBtnLoader && (
        <Modal>
          <Center data-testid="loader-modal">
            <Loader src={srcSpinner} alt={t("loading")} />
          </Center>
        </Modal>
      )}
      <TableRow data-testid="place-row">
        <PlaceNameTableCell>
          {place.name?.trim() || t("place.no_name")}
        </PlaceNameTableCell>
        <TableCell>
          <ProductTypeBadges artifacts={artifacts} />
        </TableCell>
        <OpenArtifactCell
          designCount={artifacts.length}
          artifact={artifacts[0]}
          onClick={() => handleArtifactClick(artifacts[0])}
          placeId={place.id}
          setExpandedRowId={setExpandedRowId}
          expandedRowId={expandedRowId}
        />
        <TableCell hideColumn>{artifacts[0].modifiedBy || "-"}</TableCell>
        <TableCell>{dateFormat(artifacts[0].modifiedDate)}</TableCell>
      </TableRow>
      {expandedRowId === place.id && (
        <DesignTable artifacts={artifacts} onClick={handleArtifactClick} />
      )}
    </Body>
  );
};

export default PlaceRow;
